import { render } from 'preact';
import { App } from '@/components/App';
import type { WidgetConfig, IWidgetThemeConfig, IWidgetSettings } from '@/types';
import { createApiClient } from '@/services/api';
import {
  applyTheme,
  setCachedTheme,
  scheduleStyleCollection,
} from '@/services/theme';
import {
  initConfigFromCache,
  setCachedConfig,
} from '@/services/configCache';
import {
  detectPreviewMode,
  initPreviewListeners,
  PREVIEW_THEME_DEFAULTS,
  PREVIEW_SUGGESTED_QUESTIONS,
  PREVIEW_SETTINGS_DEFAULTS,
  createPreviewMessages,
} from '@/services/previewMode';
import { deepMerge } from '@/utils/deepMerge';
import '@/styles/main.css';

import type { WidgetMode } from '@/types';

/** Public API for controlling the widget externally */
interface OnboardiWidgetAPI {
  open: () => void;
  close: () => void;
  show: () => void;
  hide: () => void;
  /** Internal: queued calls before widget init */
  _queue?: Array<['open' | 'close' | 'show' | 'hide']>;
}

/** Global config object (alternative to data attributes) */
interface OnboardiGlobalConfig {
  apiKey?: string;
  apiUrl?: string;
  mode?: WidgetMode;
  instanceId?: string;
  enableSync?: boolean;
  parentOrigin?: string;
  /** External session ID for sharing between widget instances */
  sessionId?: string;
}

declare global {
  interface Window {
    __WIDGET_CSS__?: string;
    __onboardiConfig?: OnboardiGlobalConfig;
    onboardiWidget?: OnboardiWidgetAPI;
  }
  const __ADMIN_URL__: string;
}

// Initialize deferred public API (before widget is ready)
function initDeferredAPI(): void {
  if (window.onboardiWidget) return; // Already initialized

  window.onboardiWidget = {
    _queue: [],
    open() { this._queue?.push(['open']); },
    close() { this._queue?.push(['close']); },
    show() { this._queue?.push(['show']); },
    hide() { this._queue?.push(['hide']); },
  };
}

// Initialize deferred API immediately
initDeferredAPI();

/**
 * Set up the real public API after widget is mounted.
 * Dispatches events to the widget root element for App to handle.
 * Also executes any queued calls from the deferred API.
 */
function setupRealAPI(root: HTMLElement, isEmbedded: boolean): void {
  const queuedCalls = window.onboardiWidget?._queue || [];

  // Create real API implementation
  const dispatchVisibility = (action: 'open' | 'close' | 'show' | 'hide') => {
    // In embedded mode, all visibility controls are no-ops
    if (isEmbedded) return;

    root.dispatchEvent(new CustomEvent('widget-visibility', {
      detail: { action },
      bubbles: true,
      composed: true,
    }));
  };

  window.onboardiWidget = {
    open: () => dispatchVisibility('open'),
    close: () => dispatchVisibility('close'),
    show: () => dispatchVisibility('show'),
    hide: () => dispatchVisibility('hide'),
  };

  // Execute queued calls
  for (const [action] of queuedCalls) {
    dispatchVisibility(action);
  }
}

function initWidget(): void {
  // Duplicate instance guard
  if (document.getElementById('onboarding-widget-host')) {
    console.warn('[OnboardingWidget] Widget already mounted, skipping');
    return;
  }

  // Check for preview mode first
  const previewMode = detectPreviewMode();

  const script = document.currentScript as HTMLScriptElement | null;

  // Parse URL params (for embedded mode via iframe)
  const urlParams = new URLSearchParams(window.location.search);
  const globalConfig = window.__onboardiConfig;

  // Script element is not required in preview mode, embedded mode (global config), or when URL params exist
  const hasAlternativeConfig = globalConfig?.apiKey || urlParams.get('apiKey');
  if (!script && !previewMode.isPreviewMode && !hasAlternativeConfig) {
    console.error('[OnboardingWidget] Script element not found');
    return;
  }

  // Get API key from multiple sources (priority: script attribute > global config > URL param)
  const apiKey = script?.getAttribute('data-api-key')
    || globalConfig?.apiKey
    || urlParams.get('apiKey');

  if (!apiKey && !previewMode.isPreviewMode) {
    console.error('[OnboardingWidget] data-api-key attribute is required');
    return;
  }

  // Parse mode (embedded vs floating)
  const mode: WidgetMode = (
    script?.getAttribute('data-mode')
    || globalConfig?.mode
    || urlParams.get('mode')
    || 'floating'
  ) as WidgetMode;

  // Parse other embedded mode parameters
  const instanceId = script?.getAttribute('data-instance-id')
    || globalConfig?.instanceId
    || urlParams.get('instanceId')
    || undefined;

  const enableSync = (
    script?.getAttribute('data-enable-sync') === 'true'
    || globalConfig?.enableSync === true
    || urlParams.get('enableSync') === 'true'
  );

  const parentOrigin = script?.getAttribute('data-parent-origin')
    || globalConfig?.parentOrigin
    || urlParams.get('parentOrigin')
    || undefined;

  // Parse sessionId for cross-instance sharing
  const sessionId = script?.getAttribute('data-session-id')
    || globalConfig?.sessionId
    || urlParams.get('sessionId')
    || undefined;

  // Track current settings for preview mode updates
  let currentSettings: IWidgetSettings = PREVIEW_SETTINGS_DEFAULTS;

  const config: WidgetConfig = {
    apiKey: apiKey || 'preview-mode',
    apiUrl: script?.getAttribute('data-api-url') || globalConfig?.apiUrl || window.location.origin,
    teaserDelay: parseInt(
      script?.getAttribute('data-teaser-delay') || '3000',
      10,
    ),
    companyName: script?.getAttribute('data-company-name') || undefined,
    adminUrl: __ADMIN_URL__,
    isPreviewMode: previewMode.isPreviewMode,
    previewMessages: previewMode.isPreviewMode ? createPreviewMessages(currentSettings) : undefined,
    suggestedQuestions: previewMode.isPreviewMode ? PREVIEW_SUGGESTED_QUESTIONS : undefined,
    settings: previewMode.isPreviewMode ? currentSettings : undefined,
    // Embedded mode parameters
    mode,
    instanceId,
    enableSync,
    parentOrigin,
    sessionId,
  };

  // Create host element
  const host = document.createElement('div');
  host.id = 'onboarding-widget-host';
  if (mode === 'embedded') {
    host.classList.add('embedded');
    host.style.cssText = 'width:100%;height:100%;position:relative;';
  }
  document.body.appendChild(host);

  // Attach Shadow DOM for style isolation
  const shadow = host.attachShadow({ mode: 'open' });

  // Inject CSS collected by cssToGlobalVarPlugin into shadow root
  if (window.__WIDGET_CSS__) {
    const style = document.createElement('style');
    style.textContent = window.__WIDGET_CSS__;
    shadow.appendChild(style);
  }

  // Create inner root for CSS variable selectors to work
  const root = document.createElement('div');
  root.id = 'onboarding-widget-root';
  root.className = mode === 'embedded' ? 'onboarding-widget-root embedded' : 'onboarding-widget-root';
  shadow.appendChild(root);

  // Preview mode: apply defaults and setup listeners
  if (previewMode.isPreviewMode) {
    applyTheme(root, PREVIEW_THEME_DEFAULTS);

    // Setup postMessage listener for real-time theme and settings updates
    initPreviewListeners(
      (partialConfig) => {
        // Merge partial config with defaults and apply
        const mergedConfig: IWidgetThemeConfig = {
          ...PREVIEW_THEME_DEFAULTS,
          ...partialConfig,
        };
        applyTheme(root, mergedConfig);
      },
      (state) => {
        // Dispatch custom event for App to handle state toggle
        root.dispatchEvent(new CustomEvent('widget-state-toggle', {
          detail: { state },
          bubbles: true,
        }));
      },
      (partialSettings) => {
        // Deep merge partial settings with current and re-render
        // Type assertion needed because IWidgetSettingsUpdate has nested partials
        currentSettings = deepMerge(currentSettings, partialSettings as Partial<IWidgetSettings>);
        const updatedConfig: WidgetConfig = {
          ...config,
          settings: currentSettings,
          // Regenerate preview messages with updated settings
          previewMessages: createPreviewMessages(currentSettings),
          // Also update legacy fields for compatibility
          suggestedQuestions: currentSettings.suggestedQuestions.enabled
            ? currentSettings.suggestedQuestions.items.slice(
                0,
                currentSettings.suggestedQuestions.displayCount
              )
            : [],
          teaserDelay: currentSettings.welcomeTeaser.delaySeconds * 1000,
        };
        render(<App config={updatedConfig} initialOpen={true} />, root);
      },
      previewMode.parentOrigin
    );

    // Render widget in preview mode (always open, no API calls)
    render(<App config={config} initialOpen={true} />, root);
    setupRealAPI(root, mode === 'embedded');
    return;
  }

  // Normal mode: Load from cache immediately (instant UI, no FOUC)
  const { cachedSettings, cachedThemeConfig, cachedEtag } = initConfigFromCache();

  // Apply cached config immediately
  if (cachedSettings) {
    config.settings = cachedSettings;
    const sqSettings = cachedSettings.suggestedQuestions;
    if (sqSettings.enabled && sqSettings.items.length > 0) {
      config.suggestedQuestions = sqSettings.items.slice(0, sqSettings.displayCount);
    }
    config.teaserDelay = cachedSettings.welcomeTeaser.delaySeconds * 1000;
  }
  if (cachedThemeConfig) {
    applyTheme(root, cachedThemeConfig);
  }
  const fullOpen = (window as { WIDGET_GOD?: boolean }).WIDGET_GOD === true || mode === 'embedded';

  // Render widget inside Shadow DOM (with cached config if available)
  render(<App config={config} initialOpen={fullOpen}/>, root);

  // Set up real public API
  setupRealAPI(root, mode === 'embedded');

  // Background config fetch with ETag caching
  const api = createApiClient(config.apiUrl, config.apiKey);

  api.getConfig(cachedEtag || undefined).then(result => {
    // 304 Not Modified - config unchanged, nothing to do
    if (result.status === 'not_modified') {
      return;
    }

    if (result.status === 'error') {
      // Config fetch failed - schedule style collection for first-time setup if no cache
      if (!cachedThemeConfig) {
        root.dispatchEvent(new CustomEvent('widget-theme-adapting', {
          bubbles: true,
          composed: true,
        }));

        scheduleStyleCollection(
          payload => api.submitStyles(payload),
          (newConfig, version) => {
            applyTheme(root, newConfig);
            setCachedTheme(newConfig, version);
          },
          {
            onComplete: (success) => {
              root.dispatchEvent(new CustomEvent('widget-theme-adapted', {
                detail: { success, adminUrl: config.adminUrl },
                bubbles: true,
                composed: true,
              }));
            },
          }
        );
      }
      return;
    }

    // New config received
    const { data: configResponse, etag } = result;

    // Apply full settings from server
    config.settings = configResponse.settings;

    // Update legacy fields for backward compatibility
    const sqSettings = configResponse.settings.suggestedQuestions;
    if (sqSettings.enabled && sqSettings.items.length > 0) {
      config.suggestedQuestions = sqSettings.items.slice(0, sqSettings.displayCount);
    } else {
      config.suggestedQuestions = [];
    }
    config.teaserDelay = configResponse.settings.welcomeTeaser.delaySeconds * 1000;

    // Re-render to apply settings
    render(<App config={config} />, root);

    // Handle theme
    const themeResponse = configResponse.theme;
    let themeConfigToCache: IWidgetThemeConfig | null = cachedThemeConfig;

    if (themeResponse.status === 'active' && themeResponse.config) {
      // Apply new theme
      applyTheme(root, themeResponse.config as unknown as IWidgetThemeConfig);
      themeConfigToCache = themeResponse.config as unknown as IWidgetThemeConfig;
      // Also update legacy theme cache for backward compatibility
      setCachedTheme(themeConfigToCache, themeResponse.version!);
    } else if (themeResponse.status === 'pending_regeneration' && themeResponse.config) {
      // Regeneration requested - keep current config but trigger style collection
      applyTheme(root, themeResponse.config as unknown as IWidgetThemeConfig);
      themeConfigToCache = themeResponse.config as unknown as IWidgetThemeConfig;

      root.dispatchEvent(new CustomEvent('widget-theme-adapting', {
        bubbles: true,
        composed: true,
      }));

      scheduleStyleCollection(
        payload => api.submitStyles(payload),
        (newConfig, version) => {
          applyTheme(root, newConfig);
          setCachedTheme(newConfig, version);
          // Update combined cache with new theme
          setCachedConfig(configResponse.settings, newConfig, etag);
        },
        {
          onComplete: (success) => {
            root.dispatchEvent(new CustomEvent('widget-theme-adapted', {
              detail: { success, adminUrl: config.adminUrl },
              bubbles: true,
              composed: true,
            }));
          },
        }
      );
    } else if (themeResponse.status === 'not_found') {
      // No theme exists - notify immediately and schedule style collection
      root.dispatchEvent(new CustomEvent('widget-theme-adapting', {
        bubbles: true,
        composed: true,
      }));

      scheduleStyleCollection(
        payload => api.submitStyles(payload),
        (newConfig, version) => {
          applyTheme(root, newConfig);
          setCachedTheme(newConfig, version);
          // Update combined cache with new theme
          setCachedConfig(configResponse.settings, newConfig, etag);
        },
        {
          onComplete: (success) => {
            root.dispatchEvent(new CustomEvent('widget-theme-adapted', {
              detail: { success, adminUrl: config.adminUrl },
              bubbles: true,
              composed: true,
            }));
          },
        }
      );
    }

    // Save to combined cache
    setCachedConfig(configResponse.settings, themeConfigToCache, etag);
  }).catch(() => {
    // Network error - widget continues with defaults or cached config
  });
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWidget);
} else {
  initWidget();
}
